// React Query hooks for compliance actions
// Provides mutations and queries for compliance action operations

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { complianceService } from '@/services/compliance-service';
import { complianceActionsDemoService } from '@/services/demo/compliance-actions-demo-service';
import { useFeatureSettings } from './use-feature-settings';
import { format } from 'date-fns';

// ============================================================================
// RECORD INSPECTION
// ============================================================================

export function useRecordInspection() {
  const queryClient = useQueryClient();
  const { modules } = useFeatureSettings();
  const isDemoMode = modules.compliance?.mode === 'demo';

  return useMutation({
    mutationFn: async (data: {
      assetComplianceId: number;
      inspectionDate: Date;
      inspectorName: string;
      outcome: 'Pass' | 'Fail' | 'PassWithAdvisory' | 'ConditionalPass';
      notes?: string;
      cost?: string;
      serviceProvider?: string;
      nextInspectionDate?: Date;
      files: File[];
    }) => {
      if (isDemoMode) {
        return complianceActionsDemoService.recordInspection(data);
      } else {
        // Call API and then upload files
        const result = await complianceService.recordInspection({
          assetComplianceId: data.assetComplianceId,
          inspectionDate: format(data.inspectionDate, 'yyyy-MM-dd'),
          inspectorName: data.inspectorName,
          outcome: data.outcome,
          notes: data.notes,
          cost: data.cost ? parseFloat(data.cost) : undefined,
          serviceProvider: data.serviceProvider,
          nextInspectionDate: data.nextInspectionDate
            ? format(data.nextInspectionDate, 'yyyy-MM-dd')
            : undefined,
        });

        // Upload files if any
        if (data.files.length > 0 && result.id) {
          await complianceService.uploadInspectionDocuments(result.id, data.files);
        }

        return result;
      }
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['asset-compliances'] });
      queryClient.invalidateQueries({ queryKey: ['compliance-history'] });
      queryClient.invalidateQueries({ queryKey: ['compliance-dashboard'] });
    },
  });
}

// ============================================================================
// SCHEDULE INSPECTION
// ============================================================================

export function useScheduleInspection() {
  const queryClient = useQueryClient();
  const { modules } = useFeatureSettings();
  const isDemoMode = modules.compliance?.mode === 'demo';

  return useMutation({
    mutationFn: async (data: {
      assetComplianceId: number;
      newInspectionDate: Date;
      assignedInspector?: string;
      schedulingNotes?: string;
      sendNotification: boolean;
    }) => {
      if (isDemoMode) {
        return complianceActionsDemoService.scheduleInspection(data);
      } else {
        return complianceService.scheduleInspection({
          assetComplianceId: data.assetComplianceId,
          scheduledDate: format(data.newInspectionDate, 'yyyy-MM-dd'),
          assignedInspector: data.assignedInspector,
          schedulingNotes: data.schedulingNotes,
          sendNotification: data.sendNotification,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-compliances'] });
      queryClient.invalidateQueries({ queryKey: ['compliance-history'] });
    },
  });
}

// ============================================================================
// MARK AS NON-APPLICABLE
// ============================================================================

export function useMarkNonApplicable() {
  const queryClient = useQueryClient();
  const { modules } = useFeatureSettings();
  const isDemoMode = modules.compliance?.mode === 'demo';

  return useMutation({
    mutationFn: async (data: {
      assetComplianceId: number;
      reason: string;
      detailedExplanation: string;
      effectiveDate: Date;
      expiryDate?: Date;
      reviewedBy?: string;
      files: File[];
    }) => {
      if (isDemoMode) {
        return complianceActionsDemoService.markNonApplicable(data);
      } else {
        const result = await complianceService.markNonApplicable({
          assetComplianceId: data.assetComplianceId,
          reason: data.reason,
          detailedExplanation: data.detailedExplanation,
          effectiveDate: format(data.effectiveDate, 'yyyy-MM-dd'),
          expiryDate: data.expiryDate ? format(data.expiryDate, 'yyyy-MM-dd') : undefined,
          reviewedBy: data.reviewedBy,
        });

        // Upload files if any
        if (data.files.length > 0 && result.id) {
          await complianceService.uploadExemptionDocuments(result.id, data.files);
        }

        return result;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-compliances'] });
      queryClient.invalidateQueries({ queryKey: ['compliance-history'] });
      queryClient.invalidateQueries({ queryKey: ['compliance-dashboard'] });
    },
  });
}

// ============================================================================
// DEFER DUE DATE
// ============================================================================

export function useDeferDueDate() {
  const queryClient = useQueryClient();
  const { modules } = useFeatureSettings();
  const isDemoMode = modules.compliance?.mode === 'demo';

  return useMutation({
    mutationFn: async (data: {
      assetComplianceId: number;
      currentDueDate: string;
      newDueDate: Date;
      reason: string;
      approvalRequired: boolean;
      approver?: string;
    }) => {
      if (isDemoMode) {
        return complianceActionsDemoService.deferDueDate(data);
      } else {
        return complianceService.deferDueDate({
          assetComplianceId: data.assetComplianceId,
          currentDueDate: data.currentDueDate,
          newDueDate: format(data.newDueDate, 'yyyy-MM-dd'),
          reason: data.reason,
          approvalRequired: data.approvalRequired,
          approver: data.approver,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-compliances'] });
      queryClient.invalidateQueries({ queryKey: ['compliance-history'] });
    },
  });
}

// ============================================================================
// COMPLIANCE HISTORY
// ============================================================================

export function useComplianceHistory(assetComplianceId: number | undefined) {
  const { modules } = useFeatureSettings();
  const isDemoMode = modules.compliance?.mode === 'demo';

  return useQuery({
    queryKey: ['compliance-history', assetComplianceId],
    queryFn: async () => {
      if (!assetComplianceId) return [];
      
      if (isDemoMode) {
        return complianceActionsDemoService.getHistory(assetComplianceId);
      } else {
        return complianceService.getComplianceHistory(assetComplianceId);
      }
    },
    enabled: !!assetComplianceId,
  });
}

// ============================================================================
// REVERSE EXEMPTION
// ============================================================================

export function useReverseExemption() {
  const queryClient = useQueryClient();
  const { modules } = useFeatureSettings();
  const isDemoMode = modules.compliance?.mode === 'demo';

  return useMutation({
    mutationFn: async (exemptionId: number) => {
      if (isDemoMode) {
        return complianceActionsDemoService.reverseExemption(exemptionId);
      } else {
        return complianceService.reverseExemption(exemptionId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-compliances'] });
      queryClient.invalidateQueries({ queryKey: ['compliance-history'] });
      queryClient.invalidateQueries({ queryKey: ['compliance-dashboard'] });
    },
  });
}

// ============================================================================
// EXPORT HISTORY
// ============================================================================

export function useExportHistory() {
  const { modules } = useFeatureSettings();
  const isDemoMode = modules.compliance?.mode === 'demo';

  return useMutation({
    mutationFn: async ({
      assetComplianceId,
      format,
    }: {
      assetComplianceId: number;
      format: 'pdf' | 'excel';
    }) => {
      if (isDemoMode) {
        // In demo mode, just return a mock blob
        return new Blob(['Mock export data'], { type: 'application/octet-stream' });
      } else {
        return complianceService.exportHistory(assetComplianceId, format);
      }
    },
  });
}
